import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../lib/api'
import {
  workflowApi,
  Connector,
  ConnectorConfig,
  WorkflowDetail,
  WorkflowCreate,
  WorkflowUpdate,
} from '../../lib/workflowApi'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  Mail,
  Play,
  Rss,
  Save,
  Webhook,
  Zap,
  ChevronDown,
} from 'lucide-react'

// Icônes des connecteurs
const connectorIcons: Record<string, React.ReactNode> = {
  schedule: <Clock className="w-5 h-5" />,
  manual: <Play className="w-5 h-5" />,
  webhook: <Webhook className="w-5 h-5" />,
  email: <Mail className="w-5 h-5" />,
  email_imap: <Mail className="w-5 h-5" />,
  rss_feed: <Rss className="w-5 h-5" />,
  slack: <Zap className="w-5 h-5" />,
  webhook_out: <Webhook className="w-5 h-5" />,
}

// Noms français des connecteurs
const connectorNames: Record<string, string> = {
  schedule: 'Planification (Cron)',
  manual: 'Déclenchement manuel',
  webhook: 'Webhook entrant',
  email: 'Envoyer un email',
  email_imap: 'Surveiller une boîte mail',
  rss_feed: 'Surveiller un flux RSS',
  slack: 'Envoyer sur Slack',
  webhook_out: 'Appeler un webhook',
}

type Agent = {
  id: number
  name: string
  slug: string
}

type FormData = {
  agent_id: number | null
  name: string
  description: string
  instructions: string
  trigger_connector_slug: string | null
  trigger_config: Record<string, any>
  trigger_user_config_id: number | null
  action_connector_slug: string | null
  action_config: Record<string, any>
  action_user_config_id: number | null
}

const emptyForm: FormData = {
  agent_id: null,
  name: '',
  description: '',
  instructions: '',
  trigger_connector_slug: null,
  trigger_config: {},
  trigger_user_config_id: null,
  action_connector_slug: null,
  action_config: {},
  action_user_config_id: null,
}

const STEPS = [
  { id: 1, title: 'Informations', description: 'Agent et détails du workflow' },
  { id: 2, title: 'Déclencheur', description: 'Quand exécuter le workflow' },
  { id: 3, title: 'Action', description: 'Que faire lors de l\'exécution' },
  { id: 4, title: 'Résumé', description: 'Vérifier et créer' },
]

export default function WorkflowEditorPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = id && id !== 'new'

  // Données
  const [agents, setAgents] = useState<Agent[]>([])
  const [triggerConnectors, setTriggerConnectors] = useState<Connector[]>([])
  const [actionConnectors, setActionConnectors] = useState<Connector[]>([])
  const [connectorConfigs, setConnectorConfigs] = useState<ConnectorConfig[]>([])
  const [workflow, setWorkflow] = useState<WorkflowDetail | null>(null)

  // Formulaire
  const [form, setForm] = useState<FormData>(emptyForm)
  const [step, setStep] = useState(1)

  // État UI
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Charger les données
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)

        const [agentsRes, triggersRes, actionsRes, configsRes] = await Promise.all([
          api.get('/agents').then((r) => r.data),
          workflowApi.listConnectors('trigger'),
          workflowApi.listConnectors('action'),
          workflowApi.listConfigs(),
        ])

        setAgents(agentsRes.items || [])
        setTriggerConnectors(triggersRes)
        setActionConnectors(actionsRes)
        setConnectorConfigs(configsRes)

        // Charger le workflow existant si édition
        if (isEdit) {
          const wf = await workflowApi.getWorkflow(Number(id))
          setWorkflow(wf)
          setForm({
            agent_id: wf.agent_id,
            name: wf.name,
            description: wf.description || '',
            instructions: wf.task_instructions || '',
            trigger_connector_slug: wf.trigger_connector_slug || null,
            trigger_config: wf.trigger_config || {},
            trigger_user_config_id: wf.trigger_user_config_id || null,
            action_connector_slug: wf.action_connector_slug || null,
            action_config: wf.action_config || {},
            action_user_config_id: wf.action_user_config_id || null,
          })
        }
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Erreur lors du chargement')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, isEdit])

  // Validation par étape
  const validateStep = (stepNum: number): boolean => {
    switch (stepNum) {
      case 1:
        return !!form.agent_id && !!form.name.trim()
      case 2:
        return !!form.trigger_connector_slug
      case 3:
        return !!form.action_connector_slug
      default:
        return true
    }
  }

  // Navigation
  const canGoNext = validateStep(step)
  const goNext = () => {
    if (canGoNext && step < 4) setStep(step + 1)
  }
  const goBack = () => {
    if (step > 1) setStep(step - 1)
  }

  // Sauvegarder
  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      if (isEdit) {
        const updateData = {
          name: form.name,
          description: form.description || undefined,
          task_instructions: form.instructions || undefined,
          trigger_config: form.trigger_config,
          trigger_user_config_id: form.trigger_user_config_id || undefined,
          action_config: form.action_config,
          action_user_config_id: form.action_user_config_id || undefined,
        }
        await workflowApi.updateWorkflow(Number(id), updateData)
      } else {
        const createData = {
          agent_id: form.agent_id!,
          name: form.name,
          description: form.description || undefined,
          task_instructions: form.instructions || undefined,
          trigger_connector_slug: form.trigger_connector_slug!,
          trigger_config: form.trigger_config,
          trigger_user_config_id: form.trigger_user_config_id || undefined,
          action_connector_slug: form.action_connector_slug!,
          action_config: form.action_config,
          action_user_config_id: form.action_user_config_id || undefined,
        }
        const created = await workflowApi.createWorkflow(createData as any)
        navigate(`/workflows/${created.id}`)
        return
      }

      navigate(`/workflows/${id}`)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  // Connecteur sélectionné
  const selectedTrigger = triggerConnectors.find((c) => c.slug === form.trigger_connector_slug)
  const selectedAction = actionConnectors.find((c) => c.slug === form.action_connector_slug)
  const selectedAgent = agents.find((a) => a.id === form.agent_id)

  if (loading) {
    return (
      <div className="relative overflow-hidden min-h-screen">
        <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
        <div className="flex items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500/30 border-t-emerald-500" />
        </div>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        {/* En-tête */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/workflows')}
            className="rounded-xl p-2 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900 dark:text-white">
              {isEdit ? 'Modifier le workflow' : 'Nouveau workflow'}
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {isEdit ? `Modification de "${workflow?.name}"` : 'Créez un workflow automatisé pour vos agents'}
            </p>
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div className="mb-6 rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Fermer</button>
          </div>
        )}

        {/* Indicateur d'étapes */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((s, index) => (
              <React.Fragment key={s.id}>
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition ${
                      step === s.id
                        ? 'bg-emerald-600 text-white'
                        : step > s.id
                        ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400'
                        : 'bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500'
                    }`}
                  >
                    {step > s.id ? <Check className="w-5 h-5" /> : s.id}
                  </div>
                  <div className="hidden sm:block">
                    <p className={`text-sm font-medium ${step >= s.id ? 'text-zinc-900 dark:text-white' : 'text-zinc-400 dark:text-zinc-500'}`}>
                      {s.title}
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{s.description}</p>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`hidden sm:block flex-1 mx-4 h-0.5 ${step > s.id ? 'bg-emerald-500' : 'bg-zinc-200 dark:bg-zinc-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Contenu de l'étape */}
        <div className="rounded-2xl border border-zinc-200/80 bg-white/70 p-6 backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70">
          {step === 1 && (
            <StepBasicInfo
              form={form}
              setForm={setForm}
              agents={agents}
              isEdit={!!isEdit}
            />
          )}
          {step === 2 && (
            <StepTrigger
              form={form}
              setForm={setForm}
              connectors={triggerConnectors}
              configs={connectorConfigs}
            />
          )}
          {step === 3 && (
            <StepAction
              form={form}
              setForm={setForm}
              connectors={actionConnectors}
              configs={connectorConfigs}
            />
          )}
          {step === 4 && (
            <StepReview
              form={form}
              selectedAgent={selectedAgent}
              selectedTrigger={selectedTrigger}
              selectedAction={selectedAction}
            />
          )}
        </div>

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={goBack}
            disabled={step === 1}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Précédent
          </button>
          
          {step < 4 ? (
            <button
              onClick={goNext}
              disabled={!canGoNext}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-emerald-500 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEdit ? 'Enregistrer' : 'Créer le workflow'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Étape 1: Informations de base
function StepBasicInfo({
  form,
  setForm,
  agents,
  isEdit,
}: {
  form: FormData
  setForm: (f: FormData) => void
  agents: Agent[]
  isEdit: boolean | undefined
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Informations générales</h2>
      
      {/* Agent */}
      <div>
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
          Agent <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            value={form.agent_id || ''}
            onChange={(e) => setForm({ ...form, agent_id: e.target.value ? Number(e.target.value) : null })}
            disabled={!!isEdit}
            className="w-full appearance-none rounded-xl border border-zinc-200/80 bg-white py-3 pl-4 pr-10 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:disabled:bg-zinc-900"
          >
            <option value="">Sélectionner un agent</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>{agent.name}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 pointer-events-none" />
        </div>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          L'agent qui exécutera ce workflow
        </p>
      </div>

      {/* Nom */}
      <div>
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
          Nom du workflow <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Ex: Rapport quotidien, Alerte nouveaux articles..."
          className="w-full rounded-xl border border-zinc-200/80 bg-white py-3 px-4 text-sm placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
          Description
        </label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Description optionnelle du workflow..."
          rows={2}
          className="w-full rounded-xl border border-zinc-200/80 bg-white py-3 px-4 text-sm placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
        />
      </div>

      {/* Instructions */}
      <div>
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
          Instructions pour l'agent
        </label>
        <textarea
          value={form.instructions}
          onChange={(e) => setForm({ ...form, instructions: e.target.value })}
          placeholder="Instructions spécifiques pour ce workflow (optionnel)..."
          rows={3}
          className="w-full rounded-xl border border-zinc-200/80 bg-white py-3 px-4 text-sm placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
        />
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Ces instructions seront ajoutées au contexte de l'agent lors de l'exécution
        </p>
      </div>
    </div>
  )
}

// Étape 2: Déclencheur
function StepTrigger({
  form,
  setForm,
  connectors,
  configs,
}: {
  form: FormData
  setForm: (f: FormData) => void
  connectors: Connector[]
  configs: ConnectorConfig[]
}) {
  const selectedConnector = connectors.find((c) => c.slug === form.trigger_connector_slug)

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Choisir un déclencheur</h2>
      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        Sélectionnez quand ce workflow doit être exécuté
      </p>

      {/* Liste des déclencheurs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {connectors.map((connector) => {
          const isSelected = form.trigger_connector_slug === connector.slug
          const icon = connectorIcons[connector.slug] || <Zap className="w-5 h-5" />
          const name = connectorNames[connector.slug] || connector.name

          return (
            <button
              key={connector.id}
              type="button"
              onClick={() => setForm({ ...form, trigger_connector_slug: connector.slug, trigger_config: {} })}
              className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                isSelected
                  ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20 dark:bg-emerald-900/20'
                  : 'border-zinc-200 hover:border-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-600'
              }`}
            >
              <div className={`rounded-lg p-2 ${isSelected ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'}`}>
                {icon}
              </div>
              <div>
                <p className={`font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-zinc-900 dark:text-white'}`}>
                  {name}
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {connector.description}
                </p>
              </div>
            </button>
          )
        })}
      </div>

      {/* Configuration du déclencheur */}
      {selectedConnector && (
        <div className="mt-6 rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
          <h3 className="text-sm font-medium text-zinc-900 dark:text-white mb-4">
            Configuration de "{connectorNames[selectedConnector.slug] || selectedConnector.name}"
          </h3>
          <DynamicConfigForm
            schema={selectedConnector.config_schema}
            config={form.trigger_config}
            onChange={(config) => setForm({ ...form, trigger_config: config })}
          />
        </div>
      )}
    </div>
  )
}

// Étape 3: Action
function StepAction({
  form,
  setForm,
  connectors,
  configs,
}: {
  form: FormData
  setForm: (f: FormData) => void
  connectors: Connector[]
  configs: ConnectorConfig[]
}) {
  const selectedConnector = connectors.find((c) => c.slug === form.action_connector_slug)

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Choisir une action</h2>
      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        Sélectionnez ce que le workflow doit faire lors de son exécution
      </p>

      {/* Liste des actions */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {connectors.map((connector) => {
          const isSelected = form.action_connector_slug === connector.slug
          const icon = connectorIcons[connector.slug] || <Zap className="w-5 h-5" />
          const name = connectorNames[connector.slug] || connector.name

          return (
            <button
              key={connector.id}
              type="button"
              onClick={() => setForm({ ...form, action_connector_slug: connector.slug, action_config: {} })}
              className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                isSelected
                  ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20 dark:bg-emerald-900/20'
                  : 'border-zinc-200 hover:border-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-600'
              }`}
            >
              <div className={`rounded-lg p-2 ${isSelected ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'}`}>
                {icon}
              </div>
              <div>
                <p className={`font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-zinc-900 dark:text-white'}`}>
                  {name}
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {connector.description}
                </p>
              </div>
            </button>
          )
        })}
      </div>

      {/* Configuration de l'action */}
      {selectedConnector && (
        <div className="mt-6 rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
          <h3 className="text-sm font-medium text-zinc-900 dark:text-white mb-4">
            Configuration de "{connectorNames[selectedConnector.slug] || selectedConnector.name}"
          </h3>
          <DynamicConfigForm
            schema={selectedConnector.config_schema}
            config={form.action_config}
            onChange={(config) => setForm({ ...form, action_config: config })}
          />
        </div>
      )}
    </div>
  )
}

// Étape 4: Résumé
function StepReview({
  form,
  selectedAgent,
  selectedTrigger,
  selectedAction,
}: {
  form: FormData
  selectedAgent: Agent | undefined
  selectedTrigger: Connector | undefined
  selectedAction: Connector | undefined
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Résumé du workflow</h2>
      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        Vérifiez les informations avant de créer le workflow
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Informations générales */}
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
          <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 mb-3">Informations</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-zinc-500 dark:text-zinc-400">Nom</dt>
              <dd className="font-medium text-zinc-900 dark:text-white">{form.name || '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-zinc-500 dark:text-zinc-400">Agent</dt>
              <dd className="font-medium text-zinc-900 dark:text-white">{selectedAgent?.name || '—'}</dd>
            </div>
            {form.description && (
              <div>
                <dt className="text-zinc-500 dark:text-zinc-400">Description</dt>
                <dd className="mt-1 text-zinc-900 dark:text-white">{form.description}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Déclencheur */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900/50 dark:bg-blue-900/20">
          <h3 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-3">Déclencheur</h3>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-500 p-2 text-white">
              {selectedTrigger && (connectorIcons[selectedTrigger.slug] || <Zap className="w-5 h-5" />)}
            </div>
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-100">
                {selectedTrigger ? (connectorNames[selectedTrigger.slug] || selectedTrigger.name) : '—'}
              </p>
            </div>
          </div>
          {Object.keys(form.trigger_config).length > 0 && (
            <div className="mt-3 space-y-1">
              <ConfigDisplay config={form.trigger_config} />
            </div>
          )}
        </div>

        {/* Action */}
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 dark:border-purple-900/50 dark:bg-purple-900/20 sm:col-span-2">
          <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-3">Action</h3>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-500 p-2 text-white">
              {selectedAction && (connectorIcons[selectedAction.slug] || <Zap className="w-5 h-5" />)}
            </div>
            <div>
              <p className="font-medium text-purple-900 dark:text-purple-100">
                {selectedAction ? (connectorNames[selectedAction.slug] || selectedAction.name) : '—'}
              </p>
            </div>
          </div>
          {Object.keys(form.action_config).length > 0 && (
            <div className="mt-3 space-y-1">
              <ConfigDisplay config={form.action_config} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Affichage lisible de la configuration
function ConfigDisplay({ config }: { config: Record<string, any> }) {
  // Labels français pour les champs communs
  const fieldLabels: Record<string, string> = {
    cron: 'Expression Cron',
    timezone: 'Fuseau horaire',
    description: 'Description',
    feed_url: 'URL du flux',
    folder: 'Dossier IMAP',
    to_email: 'Destinataire',
    subject_template: 'Sujet',
    body_template: 'Message',
    url: 'URL',
    message_template: 'Message',
    channel: 'Canal',
    max_emails: 'Max emails',
    max_entries: 'Max articles',
    poll_interval_seconds: 'Intervalle (sec)',
  }

  return (
    <dl className="text-xs space-y-1.5">
      {Object.entries(config).map(([key, value]) => {
        if (value === undefined || value === null || value === '') return null
        const label = fieldLabels[key] || key.replace(/_/g, ' ')
        const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
        
        return (
          <div key={key} className="flex gap-2">
            <dt className="font-medium opacity-70 min-w-[100px]">{label}:</dt>
            <dd className="opacity-90 break-all">{displayValue}</dd>
          </div>
        )
      })}
    </dl>
  )
}

// Formulaire dynamique basé sur JSON Schema
function DynamicConfigForm({
  schema,
  config,
  onChange,
}: {
  schema: Record<string, any>
  config: Record<string, any>
  onChange: (config: Record<string, any>) => void
}) {
  if (!schema || !schema.properties) {
    return <p className="text-sm text-zinc-500">Aucune configuration requise</p>
  }

  const properties = schema.properties || {}
  const required = schema.required || []

  // Labels français pour les champs communs
  const fieldLabels: Record<string, string> = {
    cron: 'Expression Cron',
    timezone: 'Fuseau horaire',
    feed_url: 'URL du flux RSS',
    folder: 'Dossier IMAP',
    to_email: 'Email destinataire',
    subject: 'Sujet de l\'email',
    body_template: 'Modèle du message',
    url: 'URL du webhook',
    message_template: 'Modèle du message',
    channel: 'Canal Slack',
    max_emails: 'Nombre max d\'emails',
    max_entries: 'Nombre max d\'articles',
    poll_interval_seconds: 'Intervalle de vérification (sec)',
  }

  return (
    <div className="space-y-4">
      {Object.entries(properties).map(([key, prop]: [string, any]) => {
        const isRequired = required.includes(key)
        const label = fieldLabels[key] || prop.description || key
        const value = config[key] ?? prop.default ?? ''

        if (prop.type === 'boolean') {
          return (
            <label key={key} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => onChange({ ...config, [key]: e.target.checked })}
                className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">{label}</span>
            </label>
          )
        }

        if (prop.type === 'integer' || prop.type === 'number') {
          return (
            <div key={key}>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                {label} {isRequired && <span className="text-red-500">*</span>}
              </label>
              <input
                type="number"
                value={value}
                onChange={(e) => onChange({ ...config, [key]: e.target.value ? Number(e.target.value) : undefined })}
                placeholder={prop.default?.toString()}
                min={prop.minimum}
                max={prop.maximum}
                className="w-full rounded-lg border border-zinc-200 bg-white py-2 px-3 text-sm focus:border-emerald-500 focus:outline-none dark:border-zinc-600 dark:bg-zinc-700 dark:text-white"
              />
            </div>
          )
        }

        // String par défaut
        return (
          <div key={key}>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            {prop.format === 'textarea' || key.includes('template') || key.includes('body') ? (
              <textarea
                value={value}
                onChange={(e) => onChange({ ...config, [key]: e.target.value || undefined })}
                placeholder={prop.examples?.[0] || prop.default}
                rows={3}
                className="w-full rounded-lg border border-zinc-200 bg-white py-2 px-3 text-sm focus:border-emerald-500 focus:outline-none dark:border-zinc-600 dark:bg-zinc-700 dark:text-white"
              />
            ) : (
              <input
                type={prop.format === 'password' ? 'password' : 'text'}
                value={value}
                onChange={(e) => onChange({ ...config, [key]: e.target.value || undefined })}
                placeholder={prop.examples?.[0] || prop.default}
                className="w-full rounded-lg border border-zinc-200 bg-white py-2 px-3 text-sm focus:border-emerald-500 focus:outline-none dark:border-zinc-600 dark:bg-zinc-700 dark:text-white"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
